import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    CircularProgress,
} from '@mui/material';
import { MdWarning, MdClose } from 'react-icons/md';
import { useTheme } from '@mui/material/styles';
import { FaCheck } from 'react-icons/fa6';
import { CiCircleInfo } from "react-icons/ci";


/**
 * Confirmation Modal Component
 * 
 * @param {Boolean} open - Control modal visibility
 * @param {Function} onClose - Called when modal is closed
 * @param {Function} onConfirm - Called when user confirms
 * @param {String} title - Modal title
 * @param {String} message - Optional message/description
 * @param {String} confirmText - Text for confirm button (default: "Confirm")
 * @param {String} cancelText - Text for cancel button (default: "Cancel")
 * @param {String} variant - "warning" | "danger" | "info" (default: "warning")
 * @param {Boolean} showCancelButton - Show/hide cancel button (default: true)
 */

const ConfirmationModal = ({
    open = false,
    onClose = () => {},
    onCancel = () => {},
    onConfirm = () => {},
    title = "Are you sure?",
    message = "",
    confirmText = "Confirm",
    cancelText = "Cancel",
    variant = "warning", // "warning", "danger", "info"
    showCloseIcon = true,
    error = null,
    loading= false,
}) => {
    const theme = useTheme();

    // Get colors based on variant
    const getVariantColors = () => {
        switch (variant) {
            case 'danger':
                return {
                    iconColor: theme.palette.error.main,
                    iconBg: 'rgba(244, 67, 54, 0.15)',
                    confirmBg: theme.palette.error.main,
                    confirmColor: '#fff',
                    icon: <MdWarning size={36} color={theme.palette.error.main}/>
                };
            case 'info':
                return {
                    iconColor: theme.palette.info.main,
                    iconBg: 'rgba(33, 150, 243, 0.15)',
                    confirmBg: theme.palette.info.main,
                    confirmColor: '#fff',
                    icon: <CiCircleInfo size={36} color={theme.palette.info.main}/>
                };
            case 'success':
                return {
                    iconColor: theme.palette.success.main,
                    iconBg: 'rgba(76, 175, 80, 0.15)',
                    confirmBg: theme.palette.success.main,
                    confirmColor: '#fff',
                    icon: <FaCheck size={36} color={theme.palette.info.main}/>
                }
            case 'warning':
            default:
                return {
                    iconColor: theme.palette.primary.main,
                    iconBg: 'rgba(254, 203, 0, 0.15)',
                    confirmBg: theme.palette.primary.main,
                    confirmColor: theme.palette.blackColor.black1,
                    icon: <MdWarning size={36} color={theme.palette.error.main}/>
                };
        }
    };

    const colors = getVariantColors();

    const handleConfirm = () => {
        onConfirm();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: theme.palette.background.paper,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 3,
                    boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.8)',
                },
            }}
            BackdropProps={{
                sx: {
                    backdropFilter: 'blur(8px)',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
            }}
        >
            {/* Close Button */}
            {showCloseIcon && (
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 12,
                        top: 12,
                        color: theme.palette.text.secondary,
                        '&:hover': {
                            color: theme.palette.text.primary,
                        },
                    }}
                >
                    <MdClose size={20} />
                </IconButton>
            )}

            {error && (
                <Box sx={{ mb: 2 }}>
                    <Typography
                        variant="body2"
                        sx={{ color: theme.palette.error.main, textAlign: 'center' }}
                    >
                        {error}
                    </Typography>
                </Box>
            )}

            {/* Icon */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    pt: 4,
                    pb: 2,
                }}
            >
                <Box
                    sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        backgroundColor: colors.iconBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `2px solid ${colors.iconColor}`,
                    }}
                >
                    {
                        colors.icon ?
                            colors.icon 
                        :
                            <MdWarning size={36} color={colors.iconColor} />
                    }
                </Box>
            </Box>

            {/* Title */}
            <DialogTitle
                sx={{
                    textAlign: 'center',
                    color: theme.palette.text.primary,
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    pb: 1,
                    pt: 1,
                }}
            >
                {title}
            </DialogTitle>

            {/* Message */}
            {message && (
                <DialogContent sx={{ textAlign: 'center', pt: 0, pb: 3 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.text.secondary,
                            lineHeight: 1.6,
                        }}
                    >
                        {message}
                    </Typography>
                </DialogContent>
            )}

            {/* Actions */}
            <DialogActions
                sx={{
                    padding: '0 24px 24px',
                    justifyContent: 'center',
                    gap: 1.5,
                }}
            >
                {cancelText && (
                    <Button
                        onClick={onCancel}
                        variant="outlined"
                        sx={{
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                            color: theme.palette.text.primary,
                            px: 3,
                            py: 1,
                            borderRadius: 2,
                            fontWeight: 600,
                            minWidth: '100px',
                            '&:hover': {
                                borderColor: 'rgba(255, 255, 255, 0.4)',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            },
                        }}
                    >
                        {cancelText}
                    </Button>
                )}
                {
                    confirmText && (
                        <Button
                            onClick={handleConfirm}
                            variant="contained"
                            disabled={loading}
                            sx={{
                                backgroundColor: colors.confirmBg,
                                color: colors.confirmColor,
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                fontWeight: 700,
                                minWidth: '100px',
                                '&:hover': {
                                    backgroundColor: colors.confirmBg,
                                    opacity: 0.9,
                                },
                            }}
                        >
                            {loading ? <CircularProgress size={20} /> : confirmText}
                            {/* {confirmText} */}
                        </Button>
                    )
                }
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmationModal;